import { useQuery } from "convex/react";
import { useMemo } from "react";
import { getSupportMetrics } from "../../backend/functions";
import {
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationState,
  AdministrationStatusSummary,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";

function reference(): string {
  return `corr_support_metrics_${crypto.randomUUID().replaceAll("-", "")}`;
}

function percentage(value: number | null): string {
  return value === null ? "Not enough completed review evidence" : `${Math.round(value * 100)}%`;
}

export function SupportPerformancePanel(): React.JSX.Element {
  const correlationId = useMemo(reference, []);
  const metrics = useQuery(getSupportMetrics, { correlationId });
  if (metrics === undefined)
    return (
      <AdministrationState
        state="loading"
        title="Preparing support performance"
        message="Checking aggregate workload and response measures."
      />
    );
  const needsAction = metrics.current.missed + metrics.current.noticeFailures;
  const monitor = metrics.current.approaching + metrics.current.unassigned;
  return (
    <section className="support-workspace">
      <AdministrationPageHeader
        title="Support performance"
        purpose="Plan support capacity from aggregate workload and response measures. No customer, household, case reference or message content is available here."
      />
      <AdministrationStatusSummary
        items={[
          {
            label: "Needs action",
            tone: "action",
            value: needsAction,
            title: "Missed aims or notice failures",
            description: "Use the support queue to review the underlying masked cases.",
          },
          {
            label: "Monitor",
            tone: "monitor",
            value: monitor,
            title: "Approaching or unassigned",
            description: "Plan capacity before these cases need intervention.",
          },
          {
            label: "Healthy",
            tone: "healthy",
            value: percentage(metrics.lastCompleteWeek.attainment),
            title: "First-review aim met",
            description: "Measured across the last complete support week.",
          },
        ]}
      />
      <AdministrationImpactPanel
        label="Customer impact"
        title={
          needsAction > 0
            ? "Some support work has missed an aim or a notice is uncertain"
            : monitor > 0
              ? "Some support work needs monitoring"
              : "No current response-risk count is recorded"
        }
        description="This aggregate view helps plan capacity; open the masked queue before acting on an individual case."
        action={
          <a className="secondary-action" href="#support/support-cases">
            Open support work queue
          </a>
        }
        tone={needsAction > 0 ? "action" : monitor > 0 ? "monitor" : "healthy"}
      />
      <div className="metric-grid">
        <Metric label="Unassigned" value={metrics.current.unassigned} />
        <Metric label="Approaching review aim" value={metrics.current.approaching} />
        <Metric label="Missed review aim" value={metrics.current.missed} />
        <Metric label="Waiting for customer" value={metrics.current.waitingForRequester} />
        <Metric label="Protected specialist work" value={metrics.current.restricted} />
        <Metric label="Support notices failed or unknown" value={metrics.current.noticeFailures} />
      </div>
      <AdministrationWorkSurface
        id="support-current-mix"
        className="support-weekly"
        title="Current workload mix"
        purpose="Use aggregate severity and category counts to decide where support capacity is needed."
        state={{
          label: metrics.current.complete ? "Complete current view" : "Incomplete current view",
          tone: metrics.current.complete ? "neutral" : "monitor",
        }}
        consequence="These figures support capacity planning only. Open the masked queue before making a decision about an individual case."
      >
        <dl>
          {metrics.current.bySeverity.map((item) => (
            <div key={item.severity}>
              <dt>{item.severity}</dt>
              <dd>{item.count}</dd>
            </div>
          ))}
          {metrics.current.byCategory.map((item) => (
            <div key={item.category}>
              <dt>{item.category.replaceAll("-", " ")}</dt>
              <dd>{item.count}</dd>
            </div>
          ))}
        </dl>
      </AdministrationWorkSurface>
      <AdministrationWorkSurface
        id="support-last-complete-week"
        className="support-weekly"
        title="Last complete week"
        purpose="Compare completed weekly outcomes without mixing them with the live queue."
        state={{
          label: metrics.lastCompleteWeek.complete
            ? "Complete reporting window"
            : "Incomplete reporting window",
          tone: metrics.lastCompleteWeek.complete ? "healthy" : "monitor",
        }}
        consequence="Use this trend for planning and assurance. It does not identify or open a customer case."
      >
        <p>
          {new Date(metrics.lastCompleteWeek.fromEpochMilliseconds).toLocaleDateString()} to{" "}
          {new Date(metrics.lastCompleteWeek.toEpochMilliseconds).toLocaleDateString()} · London
          calendar
        </p>
        <dl>
          <div>
            <dt>First-review aim met</dt>
            <dd>{percentage(metrics.lastCompleteWeek.attainment)}</dd>
          </div>
          <div>
            <dt>Review sample</dt>
            <dd>{metrics.lastCompleteWeek.reviewSample}</dd>
          </div>
          <div>
            <dt>Cases received</dt>
            <dd>{metrics.lastCompleteWeek.receivedEvents}</dd>
          </div>
          <div>
            <dt>Resolved events</dt>
            <dd>{metrics.lastCompleteWeek.resolvedEvents}</dd>
          </div>
          <div>
            <dt>Closed events</dt>
            <dd>{metrics.lastCompleteWeek.closedEvents}</dd>
          </div>
          <div>
            <dt>Resolution replies</dt>
            <dd>{metrics.lastCompleteWeek.reopenedEvents}</dd>
          </div>
        </dl>
        {!metrics.current.complete || !metrics.lastCompleteWeek.complete ? (
          <p role="status">
            This view reached its safe reporting limit. Treat the figures as incomplete.
          </p>
        ) : null}
      </AdministrationWorkSurface>
    </section>
  );
}

function Metric({ label, value }: Readonly<{ label: string; value: number }>): React.JSX.Element {
  return (
    <article>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}
